import { type FC } from 'react';
import styled from 'styled-components';

import { useFundEsgQuery } from '@/api/esg/esgApi';
import { Center } from '@/components/Center';
import { Column, Row } from '@/components/Grid';
import { ConnectPopover } from '@/components/Pretzel/ConnectPopover';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';

import { LargeText } from '../components/LargeText';
import { ScienceBasedTargetsChart } from '../components/ScienceBasedTargetsChart';

interface Props {
  isin: string;
}

export const EsgScienceBasedTargets: FC<Props> = ({ isin }) => {
  const texts = useTexts();
  const esgFundQuery = useFundEsgQuery({ isin });

  return (
    <WithGap>
      <WithGap gap="8" direction="row">
        <h2>{texts.pages.fundEsg.scienceBasedTargets.title}</h2>

        {texts.pages.fundEsg.scienceBasedTargets.helpDescription && (
          <ConnectPopover
            id="fund-esg-science-based-targets"
            description={
              texts.pages.fundEsg.scienceBasedTargets.helpDescription
            }
          />
        )}
      </WithGap>

      <Row>
        <Column $span={5}>
          <WithGap gap="16">
            <LargeText>
              {esgFundQuery.data!.scienceBasedTargetSet!.kpi.value} %
            </LargeText>

            <div className="font-body-demibold">
              {texts.pages.fundEsg.scienceBasedTargets.subtitle}
            </div>

            <p className="font-body">
              {texts.pages.fundEsg.scienceBasedTargets.description}
            </p>
          </WithGap>
        </Column>

        <Column $span={1}>{/* Empty column used for spacing. */}</Column>

        <Column $span={6}>
          <CenterStyled vertical>
            <ScienceBasedTargetsChart
              fundPercent={esgFundQuery.data!.scienceBasedTargetSet!.kpi.value}
              benchmarkPercent={
                esgFundQuery.data!.scienceBasedTargetSet!.benchmark?.value
              }
            />
          </CenterStyled>
        </Column>
      </Row>
    </WithGap>
  );
};

const CenterStyled = styled(Center)`
  height: 100%;
`;
